import Cookies from 'js-cookie'
import { useState, useEffect, Dispatch, SetStateAction } from 'react'
import { ONE_TRUST_TRACKING_GROUP } from '../constants'
import {
  parseOneTrustConsentId,
  parseOneTrustGroups
} from '../utils/parseOneTrust'

interface UseOneTrust {
  blockTrackersBeforeConsent: boolean
  setTrackingConsented: Dispatch<SetStateAction<boolean>>
  setUserId: Dispatch<SetStateAction<string>>
  oneTrustInitialized: boolean
  setOneTrustInitialized: Dispatch<SetStateAction<boolean>>
}

const setIdentifier = (userId: string) => {
  if (typeof window === 'undefined' || !window.OneTrust || !userId) {
    return
  }

  try {
    const identifiers = (userId ?? '_').split('_')
    const identifier = identifiers.length > 1 ? identifiers[1] : identifiers[0]
    const consent = Cookies.get().OptanonConsent
    const consentId = parseOneTrustConsentId(consent)
    const { setDataSubjectId, SendReceipt } = window.OneTrust

    if (
      setDataSubjectId &&
      SendReceipt &&
      identifier &&
      identifier !== consentId
    ) {
      window.OneTrust.setDataSubjectId(identifier)
      window.OneTrust.SendReceipt()
    }
  } catch (error) {
    console.warn('[OneTrust] Failed to set identifier:', error)
  }
}

const setTrackingConsentedCookie = (setTrackingCookie) =>
  Cookies.set('trackingConsented', setTrackingCookie, {
    path: '/',
    secure: true
  })

const useOneTrust = ({
  blockTrackersBeforeConsent,
  setTrackingConsented,
  setUserId,
  oneTrustInitialized,
  setOneTrustInitialized
}: UseOneTrust) => {
  const [currentUserId, setCurrentUserId] = useState<string | null>(
    Cookies.get('ajs_user_id')
  )

  useEffect(() => {
    if (process.env.STAGE !== 'prod')
      console.log('[OneTrust] Initialized', !!oneTrustInitialized)
    if (
      oneTrustInitialized &&
      typeof window !== 'undefined' &&
      window.OneTrust
    ) {
      setIdentifier(currentUserId)
      try {
        window.OneTrust.OnConsentChanged(async ({ detail }) => {
          if (process.env.STAGE !== 'prod')
            console.log('[OneTrust] Consent Changed')

          const consented =
            !blockTrackersBeforeConsent ||
            detail.includes(ONE_TRUST_TRACKING_GROUP)
          setTrackingConsented(consented)
          setTrackingConsentedCookie(consented)

          const fullUrl = new URL(
            '/api/auth/consent-change',
            window.location.origin
          )

          await fetch(fullUrl, { method: 'POST' })
          const newAjsUserId = Cookies.get('ajs_user_id')
          setCurrentUserId((prevValue) => {
            if (newAjsUserId !== prevValue) {
              setUserId(newAjsUserId)
              setIdentifier(newAjsUserId)
              return newAjsUserId
            }
            return prevValue
          })
        })
      } catch (error) {
        console.warn(
          '[OneTrust] Failed to setup consent change listener:',
          error
        )
      }
    }
  }, [oneTrustInitialized])

  useEffect(() => {
    const consent = Cookies.get().OptanonConsent
    const groups = parseOneTrustGroups(consent)
    const consented =
      !blockTrackersBeforeConsent || groups.includes(ONE_TRUST_TRACKING_GROUP)
    setTrackingConsentedCookie(consented)
    setTrackingConsented(consented)
  }, [])

  if (typeof window !== 'undefined')
    window['OptanonWrapper'] = () => {
      setOneTrustInitialized(true)
    }
}

export default useOneTrust
